import { env } from "@/lib/env";

export type BrandTerms = {
  station: string;
  stationPlural: string;
  stationGroup: string;
  stationGroupPlural: string;
  stationSubGroup: string;
  stationSubGroupPlural: string;
  stationAssignment: string;
  rank: string;
  rankPlural: string;
};

export type BrandConfig = {
  name: string;
  shortName: string;
  organization: string;
  supportEmail: string;
  trainingEmail: string;
  supportPhone?: string;
  domain: string;
  emailDomains: string[];
  slug: string;
  slugVariants: string[];
  logoPath: string;
  logoLightPath: string;
  logoDarkPath: string;
  faviconPath: string;
  tagline?: string;
  terms: BrandTerms;
  terminology: Record<string, string>;
  timeZone: string;
  appointingAuthority: {
    show: boolean;
    defaultValue: string;
  };
};

const DEFAULT_BRAND_NAME = "IBSuite";
const DEFAULT_ORGANIZATION = "Infobyte Solution";
const DEFAULT_DOMAIN = "ibpersonnel.local";
const DEFAULT_SUPPORT_EMAIL = "support@ibpersonnel.local";
const DEFAULT_LEGACY_SLUGS: string[] = [];

function getEnv(name: string, fallback?: string) {
  let value = process.env[name];
  if (value && value.length > 0) {
    // Strip surrounding quotes and whitespace
    value = value.trim();
    while (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1).trim();
    }
    return value;
  }
  return fallback;
}

const DEFAULT_BRAND_TERMS: BrandTerms = {
  station: "Station",
  stationPlural: "Stations",
  stationGroup: "Station Group",
  stationGroupPlural: "Station Groups",
  stationSubGroup: "Station Subgroup",
  stationSubGroupPlural: "Station Subgroups",
  stationAssignment: "Station Assignment",
  rank: "Designation",
  rankPlural: "Designations",
};

function buildBrandTerms(): BrandTerms {
  return {
    station: env.client.NEXT_PUBLIC_BRAND_STATION_NAME,
    stationPlural: env.client.NEXT_PUBLIC_BRAND_STATION_PLURAL,
    stationGroup: env.client.NEXT_PUBLIC_BRAND_STATION_GROUP_NAME,
    stationGroupPlural: env.client.NEXT_PUBLIC_BRAND_STATION_GROUP_PLURAL,
    stationSubGroup: getEnv("NEXT_PUBLIC_BRAND_STATION_SUBGROUP_NAME", DEFAULT_BRAND_TERMS.stationSubGroup)!,
    stationSubGroupPlural: getEnv(
      "NEXT_PUBLIC_BRAND_STATION_SUBGROUP_PLURAL",
      DEFAULT_BRAND_TERMS.stationSubGroupPlural,
    )!,
    stationAssignment: getEnv(
      "NEXT_PUBLIC_BRAND_STATION_ASSIGNMENT_NAME",
      DEFAULT_BRAND_TERMS.stationAssignment,
    )!,
    rank: env.client.NEXT_PUBLIC_BRAND_RANK_NAME,
    rankPlural: env.client.NEXT_PUBLIC_BRAND_RANK_PLURAL,
  };
}

export const brandTerms = buildBrandTerms();

function parseCsv(value?: string | null) {
  if (!value) {
    return [];
  }
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeSlug(value?: string | null) {
  if (!value) {
    return undefined;
  }
  const normalized = value
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
  return normalized.length > 0 ? normalized : undefined;
}

function addSlugVariant(target: Set<string>, value?: string | null) {
  const normalized = normalizeSlug(value);
  if (!normalized) {
    return;
  }
  if (!target.has(normalized)) {
    target.add(normalized);
  }
  const compact = normalized.replace(/-/g, "");
  if (compact.length > 0 && !target.has(compact)) {
    target.add(compact);
  }
}

function sanitizeDomain(value?: string | null) {
  if (!value) {
    return undefined;
  }
  const cleaned = value
    .toString()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/\s+/g, "");
  return cleaned.length > 0 ? cleaned : undefined;
}

function appendGovDomains(target: Set<string>, slugVariant: string) {
  if (!slugVariant) {
    return;
  }
  const normalized = slugVariant.replace(/^\.+|\.+$/g, "");
  if (!normalized) {
    return;
  }
  const candidates = [`${normalized}.gov.ng`, `${normalized}.gov`];
  for (const candidate of candidates) {
    const sanitized = sanitizeDomain(candidate);
    if (sanitized) {
      target.add(sanitized);
    }
  }
}

export function getBrandConfig(): BrandConfig {
  let name = env.client.NEXT_PUBLIC_BRAND_NAME;
  if (name === "NAMA") name = "NBISuite";
  let shortName = env.client.NEXT_PUBLIC_BRAND_SHORT_NAME || name;
  if (shortName === "NAMA") shortName = "NSIB";
  const organization = env.client.NEXT_PUBLIC_BRAND_ORGANIZATION;

  const slugSeed = getEnv("NEXT_PUBLIC_BRAND_SLUG", shortName || name);
  const slugSet = new Set<string>();
  addSlugVariant(slugSet, slugSeed);
  addSlugVariant(slugSet, shortName);
  addSlugVariant(slugSet, name);
  if (slugSeed) {
    addSlugVariant(slugSet, `${slugSeed} csc`);
  } else if (shortName) {
    addSlugVariant(slugSet, `${shortName} csc`);
  }
  const slugAliasesFromEnv = parseCsv(getEnv("NEXT_PUBLIC_BRAND_SLUG_ALIASES"));
  slugAliasesFromEnv.forEach((alias) => addSlugVariant(slugSet, alias));
  DEFAULT_LEGACY_SLUGS.forEach((legacy) => addSlugVariant(slugSet, legacy));
  const slugVariants = Array.from(slugSet);
  const slug = slugVariants[0] ?? "brand";

  const domain =
    sanitizeDomain(getEnv("NEXT_PUBLIC_BRAND_EMAIL_DOMAIN", DEFAULT_DOMAIN)) ??
    sanitizeDomain(DEFAULT_DOMAIN) ??
    "notspecified.gov.ng";
  let logoPath = env.client.NEXT_PUBLIC_BRAND_LOGO;
  if (logoPath?.includes("nama")) logoPath = ""; // use default

  // Fix for potential misconfiguration where "images/" is prepended to "imagesnama"
  if (logoPath?.includes("images/imagesnama")) {
    logoPath = logoPath.replace("images/imagesnama", "imagesnama");
  }

  const logoLightPath = env.client.NEXT_PUBLIC_BRAND_LOGO_LIGHT || logoPath;
  const logoDarkPath = env.client.NEXT_PUBLIC_BRAND_LOGO_DARK || logoPath;
  const faviconPath = env.client.NEXT_PUBLIC_BRAND_FAVICON;

  const derivedSupportEmail = `support@${domain}`;
  const supportEmail =
    getEnv("NEXT_PUBLIC_BRAND_SUPPORT_EMAIL", derivedSupportEmail) ??
    derivedSupportEmail ??
    DEFAULT_SUPPORT_EMAIL;
  const trainingEmail = getEnv("NEXT_PUBLIC_BRAND_TRAINING_EMAIL", supportEmail);
  const supportPhone = getEnv("NEXT_PUBLIC_BRAND_SUPPORT_PHONE");
  const tagline = getEnv("NEXT_PUBLIC_BRAND_TAGLINE");

  const emailDomainsSet = new Set<string>();
  emailDomainsSet.add(domain);
  const emailDomainAliases = parseCsv(getEnv("NEXT_PUBLIC_BRAND_EMAIL_DOMAIN_ALIASES"));
  emailDomainAliases.forEach((alias) => {
    const sanitized = sanitizeDomain(alias);
    if (sanitized) {
      emailDomainsSet.add(sanitized);
    }
  });
  slugVariants.forEach((variant) => appendGovDomains(emailDomainsSet, variant));
  const emailDomains = Array.from(emailDomainsSet);

  // Terminology mappings for UI text customization
  // Add custom term mappings here as needed (e.g., 'Personnel Information': 'Bio-Data')
  const terminology: Record<string, string> = {
    'Personal Information': 'Bio-Data',
    'Leave Certificate': 'Leave Advice',
    'File Number': 'Staff Number',
    'Location': 'Station',
    'Qualifications': 'Qualifications',
    'Qualifications & Edu': 'Qualifications',
    'Certificate': 'License',
  };

  return {
    name,
    shortName,
    organization,
    domain,
    emailDomains,
    slug,
    slugVariants,
    supportEmail,
    trainingEmail: trainingEmail ?? supportEmail,
    supportPhone: supportPhone ?? undefined,
    tagline: tagline ?? undefined,
    terms: brandTerms,
    terminology,
    logoPath,
    logoLightPath,
    logoDarkPath,
    faviconPath,
    timeZone: "Africa/Lagos",
    appointingAuthority: {
      show: true,
      defaultValue: "CSC",
    },
  };
}

export const brand = getBrandConfig();

export function buildBrandEmail(localPart: string, preferredDomain?: string) {
  const targetLocalPart = (localPart ?? "").trim();
  if (!targetLocalPart) {
    return brand.supportEmail;
  }

  if (targetLocalPart.includes("@")) {
    return targetLocalPart;
  }

  const domainCandidates = preferredDomain
    ? [preferredDomain, ...brand.emailDomains]
    : brand.emailDomains;

  const domainToUse =
    domainCandidates
      .map((candidate) => sanitizeDomain(candidate))
      .find((candidate): candidate is string => Boolean(candidate)) ?? brand.domain;

  return `${targetLocalPart}@${domainToUse}`;
}
